import { useCallback, useEffect, useMemo } from "react";
import { StringParam, useQueryParam } from "use-query-params";
import { Entity } from "@common/types/entity";
import { useEntitiesContexts } from "@hooks/useEntitiesContexts";
import { useConsolidationReportContext } from "@components/accounting/consolidationReports/useConsolidationReportContext";
import {
    buildReportingConsolidationScope,
    buildReportingEntityScope,
    isEligibleReportingEntity,
    parseReportingRouteScope,
    REPORTING_SCOPE_QUERY_PARAM,
} from "./reportingScope";

interface ReportingEntityScope {
    entities: readonly Entity[];
    isScopeRequested: boolean;
    selectedEntity?: Entity;
    selectedConsolidationGroupId?: string;
    setSelectedEntityId: (entityId?: number) => void;
}

function resolveCanonicalScope({
    selectedEntityId,
    selectedConsolidationGroupId,
}: {
    selectedEntityId?: number;
    selectedConsolidationGroupId?: string;
}): string | undefined {
    if (selectedEntityId !== undefined) {
        return buildReportingEntityScope(selectedEntityId);
    }
    if (selectedConsolidationGroupId !== undefined) {
        return buildReportingConsolidationScope(selectedConsolidationGroupId);
    }
    return undefined;
}

export function useReportingEntityScope(): ReportingEntityScope {
    const entityContexts = useEntitiesContexts({
        onlyBusiness: true,
        excludeMockEntities: true,
    });
    const consolidationResult = useConsolidationReportContext();
    const entities = useMemo(
        () =>
            (entityContexts ?? [])
                .map(({ entity }) => entity)
                .filter(isEligibleReportingEntity),
        [entityContexts],
    );
    const [scopeParam, setScopeParam] = useQueryParam(
        REPORTING_SCOPE_QUERY_PARAM,
        StringParam,
    );
    const routeScope = parseReportingRouteScope(scopeParam);
    const selectedEntity =
        routeScope?.kind === "entity"
            ? entities.find((entity) => entity.id === routeScope.id)
            : undefined;
    const selectedConsolidationGroupId =
        routeScope?.kind === "consolidationGroup" &&
        consolidationResult.status === "ready" &&
        consolidationResult.context.group.id === routeScope.id
            ? routeScope.id
            : undefined;
    const canonicalScope = resolveCanonicalScope({
        selectedEntityId: selectedEntity?.id,
        selectedConsolidationGroupId,
    });
    const isGroupScopePending =
        routeScope?.kind === "consolidationGroup" &&
        consolidationResult.status === "loading";
    const isResolved = entityContexts !== undefined && !isGroupScopePending;

    useEffect(() => {
        if (
            isResolved &&
            scopeParam !== undefined &&
            scopeParam !== canonicalScope
        ) {
            setScopeParam(canonicalScope, "replaceIn");
        }
    }, [canonicalScope, isResolved, scopeParam, setScopeParam]);

    const setSelectedEntityId = useCallback(
        (entityId?: number) => {
            setScopeParam(
                entityId ? buildReportingEntityScope(entityId) : undefined,
                "replaceIn",
            );
        },
        [setScopeParam],
    );

    return {
        entities,
        isScopeRequested: scopeParam !== undefined,
        selectedEntity,
        selectedConsolidationGroupId,
        setSelectedEntityId,
    };
}
